import { useEffect, useRef, useState, type ReactNode } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAdminAuth } from '../lib/auth'
import { useLogout } from '../lib/api'
import { useTheme } from '../lib/theme'

/** Sidebar nav items, grouped by section per the admin design reference. */
const NAV_MAIN = [
  { to: '/overview',  label: 'Dashboard',   page: 'dashboard',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg> },
  { to: '/users',     label: 'Users',       page: 'users',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21 v-2 a4 4 0 0 0 -4 -4 H6 a4 4 0 0 0 -4 4 v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21 v-2 a4 4 0 0 0 -3 -3.87 M16 3.13 a4 4 0 0 1 0 7.75"/></svg> },
]

const NAV_QUEUES = [
  { to: '/payouts',   label: 'Payouts',     badge: 'payouts',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10 h18 M7 15 h4"/></svg> },
  { to: '/fraud',     label: 'Fraud flags', badge: 'fraud',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9 v4 M12 17 v.01 M10.29 3.86 L1.82 18 a2 2 0 0 0 1.71 3 h16.94 a2 2 0 0 0 1.71 -3 L13.71 3.86 a2 2 0 0 0 -3.42 0 z"/></svg> },
  { to: '/reviews',   label: 'KYC reviews', badge: 'kyc',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="M5 18 q4 -4 8 0 M14 9 h5 M14 13 h5"/></svg> },
]

const NAV_INSIGHTS = [
  { to: '/audit',     label: 'Audit log',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2 H6 a2 2 0 0 0 -2 2 v16 a2 2 0 0 0 2 2 h12 a2 2 0 0 0 2 -2 V8 z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg> },
  { to: '/settings',  label: 'Settings',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15 a1.7 1.7 0 0 0 .4 1.9 l.1 .1 a2 2 0 1 1 -2.8 2.8 l-.1 -.1 a1.7 1.7 0 0 0 -1.9 -.4 1.7 1.7 0 0 0 -1 1.5 V21 a2 2 0 1 1 -4 0 v-.1 a1.7 1.7 0 0 0 -1 -1.5 1.7 1.7 0 0 0 -1.9 .4 l-.1 .1 a2 2 0 1 1 -2.8 -2.8 l.1 -.1 a1.7 1.7 0 0 0 .4 -1.9 1.7 1.7 0 0 0 -1.5 -1 H3 a2 2 0 1 1 0 -4 h.1 a1.7 1.7 0 0 0 1.5 -1 1.7 1.7 0 0 0 -.4 -1.9 l-.1 -.1 a2 2 0 1 1 2.8 -2.8 l.1 .1 a1.7 1.7 0 0 0 1.9 .4 H9 a1.7 1.7 0 0 0 1 -1.5 V3 a2 2 0 1 1 4 0 v.1 a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9 -.4 l.1 -.1 a2 2 0 1 1 2.8 2.8 l-.1 .1 a1.7 1.7 0 0 0 -.4 1.9 V9 a1.7 1.7 0 0 0 1.5 1 H21 a2 2 0 1 1 0 4 h-.1 a1.7 1.7 0 0 0 -1.5 1 z"/></svg> },
]

function NavItem({ to, label, badge, icon }: { to: string; label: string; badge?: string; icon: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => 'nav-item' + (isActive ? ' nav-item--active' : '')}
    >
      <span className="nav-item__icon">{icon}</span>
      <span className="nav-item__label">{label}</span>
      {badge && <span className="nav-item__badge" data-badge={badge}>—</span>}
    </NavLink>
  )
}

function pageKey(path: string): string {
  const m = path.match(/^\/([^/]+)/)
  return m ? m[1] : 'dashboard'
}

export function AdminAppShell({ children }: { children: ReactNode }) {
  const { user } = useAdminAuth()
  const logout = useLogout()
  const nav = useNavigate()
  const loc = useLocation()
  const theme = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close the dropdown on outside click / Escape — replaces the native
  // <details> behaviour which iOS Safari sometimes mis-handles.
  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])
  // Close menu when route changes.
  useEffect(() => { setMenuOpen(false) }, [loc.pathname])
  const page = pageKey(loc.pathname)
  // Map a few route → topbar breadcrumb labels matching the mockup
  const breadcrumb: Record<string, string> = {
    overview:  'Admin · Dashboard',
    users:     'Admin · Users',
    payouts:   'Admin · Payouts',
    fraud:     'Admin · Fraud flags',
    reviews:   'Admin · KYC reviews',
    audit:     'Admin · Audit log',
    settings:  'Admin · Settings',
  }

  const initials = (user?.displayName ?? user?.email ?? '?')
    .split(/\s|@/)
    .map((s) => s.charAt(0).toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join('')

  return (
    <div className="admin-app" data-page={page}>
      <aside className="admin-app__sidebar admin-sidebar" aria-label="Admin navigation">
        <a href="/" className="admin-sidebar__logo" aria-label="Rival Admin">
          <img src="/assets/img/wordmark-light.svg" alt="Rival Prop Firm" className="admin-sidebar__logo-img admin-sidebar__logo-img--full-light" />
          <img src="/assets/img/wordmark-dark.svg"  alt="Rival Prop Firm" className="admin-sidebar__logo-img admin-sidebar__logo-img--full-dark" />
          {/* Transparent-background variant — icon-dark.svg has an opaque navy
              rect we don't want over the sidebar. icon-square.svg keeps only
              the paths. */}
          <img src="/assets/img/icon-light.svg"  alt="" aria-hidden className="admin-sidebar__logo-img admin-sidebar__logo-img--square-light" />
          <img src="/assets/img/icon-square.svg" alt="" aria-hidden className="admin-sidebar__logo-img admin-sidebar__logo-img--square-dark" />
        </a>

        <nav className="admin-sidebar__section" aria-label="Main">
          {NAV_MAIN.map((n) => <NavItem key={n.to} {...n} />)}
        </nav>

        <nav className="admin-sidebar__section" aria-label="Review queues">
          <span className="admin-sidebar__section-label">REVIEW QUEUES</span>
          {NAV_QUEUES.map((n) => <NavItem key={n.to} {...n} />)}
        </nav>

        <nav className="admin-sidebar__section" aria-label="Insights">
          <span className="admin-sidebar__section-label">INSIGHTS</span>
          {NAV_INSIGHTS.map((n) => <NavItem key={n.to} {...n} />)}
        </nav>

        <div className="sidebar__spacer" style={{ flex: 1 }} />

        <nav className="admin-sidebar__section" aria-label="Account">
          <button
            type="button"
            className="nav-item"
            style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
            onClick={() => logout.mutate(undefined, { onSuccess: () => nav('/login') })}
          >
            <span className="nav-item__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21 H5 a2 2 0 0 1 -2 -2 V5 a2 2 0 0 1 2 -2 h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </span>
            <span className="nav-item__label">Sign out</span>
          </button>
        </nav>
      </aside>

      <header className="admin-app__topbar topbar">
        <div className="topbar__breadcrumb">
          <span className="topbar__breadcrumb-current">{breadcrumb[page] ?? 'Admin'}</span>
        </div>
        <div className="topbar__spacer" />

        <span className="admin-mode-pill">
          <span className="admin-mode-pill__dot" aria-hidden="true"></span>
          Admin mode
        </span>

        <button
          type="button"
          className="theme-toggle"
          aria-label="Toggle theme"
          onClick={theme.toggle}
          title={theme.resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme.resolved === 'dark' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden>
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2 v2 M12 20 v2 M4.93 4.93 l1.41 1.41 M17.66 17.66 l1.41 1.41 M2 12 h2 M20 12 h2 M4.93 19.07 l1.41 -1.41 M17.66 6.34 l1.41 -1.41" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden>
              <path d="M21 12.8 A9 9 0 1 1 11.2 3 a7 7 0 0 0 9.8 9.8 z" />
            </svg>
          )}
        </button>

        <div ref={menuRef} className="profile-menu" style={{ position: 'relative' }}>
          <button
            type="button"
            className="avatar"
            aria-label="Account menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32, height: 32, borderRadius: '50%',
              background: 'linear-gradient(135deg,#dc2626,#ef4444)',
              color: '#fff', fontWeight: 700, fontSize: 12,
              cursor: 'pointer', border: 0,
            }}
          >{initials || '?'}</button>
          {menuOpen && (
            <div className="profile-menu__panel" role="menu" style={{ display: 'flex' }}>
              <div className="profile-menu__header">
                <span className="profile-menu__name">{user?.displayName ?? user?.email ?? 'Admin'}</span>
                <span className="profile-menu__email">{user?.role ?? ''}</span>
              </div>
              <NavLink to="/settings" className="profile-menu__option" role="menuitem">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15 a1.7 1.7 0 0 0 .4 1.9 l.1 .1 a2 2 0 1 1 -2.8 2.8 l-.1 -.1 a1.7 1.7 0 0 0 -1.9 -.4 1.7 1.7 0 0 0 -1 1.5 V21 a2 2 0 1 1 -4 0 v-.1 a1.7 1.7 0 0 0 -1 -1.5 1.7 1.7 0 0 0 -1.9 .4 l-.1 .1 a2 2 0 1 1 -2.8 -2.8 l.1 -.1 a1.7 1.7 0 0 0 .4 -1.9 1.7 1.7 0 0 0 -1.5 -1 H3 a2 2 0 1 1 0 -4 h.1 a1.7 1.7 0 0 0 1.5 -1 1.7 1.7 0 0 0 -.4 -1.9 l-.1 -.1 a2 2 0 1 1 2.8 -2.8 l.1 .1 a1.7 1.7 0 0 0 1.9 .4 H9 a1.7 1.7 0 0 0 1 -1.5 V3 a2 2 0 1 1 4 0 v.1 a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9 -.4 l.1 -.1 a2 2 0 1 1 2.8 2.8 l-.1 .1 a1.7 1.7 0 0 0 -.4 1.9 V9 a1.7 1.7 0 0 0 1.5 1 H21 a2 2 0 1 1 0 4 h-.1 a1.7 1.7 0 0 0 -1.5 1 z"/>
                </svg>
                Settings
              </NavLink>
              <div className="profile-menu__divider" />
              <button
                type="button"
                className="profile-menu__option profile-menu__option--danger"
                role="menuitem"
                onClick={() => { setMenuOpen(false); logout.mutate(undefined, { onSuccess: () => nav('/login') }) }}
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 0, cursor: 'pointer' }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <path d="M9 21 H5 a2 2 0 0 1 -2 -2 V5 a2 2 0 0 1 2 -2 h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="admin-app__main">{children}</main>
    </div>
  )
}
